import React from 'react';

import { DataClassifiers } from '@ariksa/data-scanning/api';
import {
  Box,
  BoxProps,
  Center,
  HStack,
  Stack,
  WrapItem,
} from '@chakra-ui/react';
import { map } from 'lodash';
import { colorOpacity, customTheme } from 'theme';

import { CustomTooltip } from 'components/DataDisplay/Tooltip/CustomTooltip';
import { formatTooltip } from 'components/DataDisplay/Tooltip/utils';
import { getIcon } from 'components/Icons/Components';

import { Tag, TagStyles } from '../Tag';

import { getDataTypeDisplayName } from './utils';

export interface DataTypeTagRenderOptions {
  classifiers?: DataClassifiers[];
  displayName?: string;
  labels?: string[];
  labelProps?: BoxProps;
  labelIndex?: number;
  showIcon?: boolean;
  tagStyles?: TagStyles;
  color?: string;
}

export const renderTagLabel = (
  dataType: string,
  {
    classifiers = [],
    displayName,
    labelProps = {},
    labelIndex,
    showIcon = true,
    color,
  }: DataTypeTagRenderOptions = {},
) => {
  const icon = getIcon(dataType);
  const name = displayName ?? getDataTypeDisplayName(dataType, classifiers);

  return (
    <HStack spacing={1} minW={0} maxW="full">
      {showIcon && icon && (
        <Box
          boxSize={dataType === 'ADDRESS' ? 6 : 5}
          color={color || customTheme.colors.primary}
          // Never shrink the icon in a tight tag; the label truncates instead.
          flexShrink={0}
        >
          <Center boxSize="full">{icon}</Center>
        </Box>
      )}
      <Box
        data-data-type-label-index={labelIndex}
        minW={0}
        overflow="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
        {...labelProps}
      >
        {name}
      </Box>
    </HStack>
  );
};

export const renderDataTypeTag = (
  dataType: string,
  {
    classifiers = [],
    displayName,
    labels,
    labelProps,
    labelIndex,
    showIcon = true,
    color = customTheme.colors.primary,
    tagStyles,
  }: DataTypeTagRenderOptions = {},
) => {
  const name = displayName ?? getDataTypeDisplayName(dataType, classifiers);
  const tooltipContent = (
    <Stack>
      {map(labels, (o, index) => (
        <Box key={`${o}-${index}`}>{o}</Box>
      ))}
    </Stack>
  );

  return (
    // Keep WrapItem as the outer element so visible tags match the measured two-row layout.
    <WrapItem key={dataType}>
      <CustomTooltip
        label={formatTooltip({
          header: name,
          content: tooltipContent,
          width: 'fit-content',
        })}
      >
        <Tag
          label={renderTagLabel(dataType, {
            classifiers,
            displayName: name,
            labelIndex,
            labelProps,
            showIcon,
            color,
          })}
          styles={{
            ...tagStyles,
            tag: {
              bg: colorOpacity(color, 0.1),
              border: 'none',
              py: dataType === 'ADDRESS' ? '1.5px' : '3px',
              ...tagStyles?.tag,
            },
            label: {
              color,
              ...tagStyles?.label,
            },
          }}
        />
      </CustomTooltip>
    </WrapItem>
  );
};
